/**
 * AWS DynamoDB User Service
 * Handles user profile storage and retrieval from DynamoDB
 */

import {
    DynamoDBDocumentClient,
    PutCommand,
    GetCommand,
    UpdateCommand,
    DeleteCommand,
    ScanCommand,
    PutCommandInput,
    GetCommandInput,
    UpdateCommandInput
} from '@aws-sdk/lib-dynamodb';
import awsConfig from '../config/aws-config';
import { getTrackedDynamoDBClient } from './aws-core';

export class AWSUserService {
    private tableName = awsConfig.dynamodb.TABLES.USERS;

    /**
     * Get or create DynamoDB client
     */
    private getClient(): DynamoDBDocumentClient {
        return getTrackedDynamoDBClient();
    }

    /**
     * Create or update user profile in DynamoDB
     */
    async saveUserProfile(userProfile: any): Promise<void> {
        try {
            const client = this.getClient();

            const params: PutCommandInput = {
                TableName: this.tableName,
                Item: {
                    ...userProfile,
                    updatedAt: new Date().toISOString(),
                },
            };

            const command = new PutCommand(params);
            await client.send(command);

            console.log('User profile saved to DynamoDB:', userProfile.userId);
        } catch (error) {
            console.error('Error saving user profile to DynamoDB:', error);
            throw error;
        }
    }

    /**
     * Get user profile from DynamoDB
     */
    async getUserProfile(userId: string): Promise<any> {
        try {
            const client = this.getClient();

            const params: GetCommandInput = {
                TableName: this.tableName,
                Key: { userId },
            };

            const command = new GetCommand(params);
            const response = await client.send(command);

            return response.Item;
        } catch (error) {
            console.error('Error getting user profile from DynamoDB:', error);
            throw error;
        }
    }

    /**
     * Update specific fields in user profile
     */
    async updateUserProfile(userId: string, updates: any): Promise<void> {
        try {
            const client = this.getClient();

            const updateExpressions: string[] = [];
            const expressionAttributeNames: Record<string, string> = {};
            const expressionAttributeValues: Record<string, any> = {};

            Object.keys(updates).forEach((key, index) => {
                const placeholder = `#field${index}`;
                const valuePlaceholder = `:value${index}`;
                updateExpressions.push(`${placeholder} = ${valuePlaceholder}`);
                expressionAttributeNames[placeholder] = key;
                expressionAttributeValues[valuePlaceholder] = updates[key];
            });

            // Always update the updatedAt timestamp
            updateExpressions.push('#updatedAt = :updatedAt');
            expressionAttributeNames['#updatedAt'] = 'updatedAt';
            expressionAttributeValues[':updatedAt'] = new Date().toISOString();

            const params: UpdateCommandInput = {
                TableName: this.tableName,
                Key: { userId },
                UpdateExpression: `SET ${updateExpressions.join(', ')}`,
                ExpressionAttributeNames: expressionAttributeNames,
                ExpressionAttributeValues: expressionAttributeValues,
            };

            const command = new UpdateCommand(params);
            await client.send(command);

            console.log('User profile updated in DynamoDB:', userId);
        } catch (error) {
            console.error('Error updating user profile in DynamoDB:', error);
            throw error;
        }
    }

    /**
     * Check if user profile exists in DynamoDB
     */
    async userProfileExists(userId: string): Promise<boolean> {
        try {
            const profile = await this.getUserProfile(userId);
            return !!profile;
        } catch (error) {
            return false;
        }
    }

    /**
     * Create a support user invite
     * Stores an invite record in the Users table with key "invite:<email>"
     */
    async createSupportInvite(email: string, role: string, addedBy: string): Promise<void> {
        try {
            const inviteId = `invite:${email.toLowerCase()}`;
            const inviteData = {
                userId: inviteId,
                email: email.toLowerCase(),
                role,
                status: 'invited',
                addedBy,
                createdAt: new Date().toISOString(),
                type: 'support_invite'
            };

            await this.saveUserProfile(inviteData);
        } catch (error) {
            console.error('Error creating support invite:', error);
            throw error;
        }
    }

    /**
     * Get support invite details
     */
    async getSupportInvite(email: string): Promise<any> {
        try {
            const inviteId = `invite:${email.toLowerCase()}`;
            return await this.getUserProfile(inviteId);
        } catch (error) {
            return null;
        }
    }

    /**
     * Delete support invite (consumed or cancelled)
     */
    async deleteInvite(email: string): Promise<void> {
        try {
            const client = this.getClient();
            const inviteId = `invite:${email.toLowerCase()}`;

            await client.send(new DeleteCommand({
                TableName: this.tableName,
                Key: { userId: inviteId }
            }));
        } catch (error) {
            console.error('Error deleting invite:', error);
        }
    }

    /**
     * Create a staff invite (Workspace Invite)
     * Stores an invite record linking a staff email to a tenantId
     */
    async createStaffInvite(email: string, role: string, tenantId: string): Promise<void> {
        try {
            const inviteId = `staff_invite:${email.toLowerCase()}`;
            const inviteData = {
                userId: inviteId,
                email: email.toLowerCase(),
                role,
                status: 'invited',
                tenantId,
                createdAt: new Date().toISOString(),
                type: 'staff_invite'
            };

            await this.saveUserProfile(inviteData);
        } catch (error) {
            console.error('Error creating staff invite:', error);
            throw error;
        }
    }

    /**
     * Get staff invite details
     */
    async getStaffInvite(email: string): Promise<any> {
        try {
            const inviteId = `staff_invite:${email.toLowerCase()}`;
            return await this.getUserProfile(inviteId);
        } catch (error) {
            return null;
        }
    }

    /**
     * Delete staff invite
     */
    async deleteStaffInvite(email: string): Promise<void> {
        try {
            const client = this.getClient();
            const inviteId = `staff_invite:${email.toLowerCase()}`;

            await client.send(new DeleteCommand({
                TableName: this.tableName,
                Key: { userId: inviteId }
            }));
        } catch (error) {
            console.error('Error deleting staff invite:', error);
        }
    }

    /**
     * List all users (Scan) - Super Admin Only
     * Warning: Expensive operation, use with caution or implement pagination
     */
    async listAllUsers(): Promise<any[]> {
        try {
            const client = this.getClient();
            const command = new ScanCommand({
                TableName: this.tableName,
            });

            const response = await client.send(command);
            return response.Items || [];
        } catch (error) {
            console.error('Error listing users:', error);
            throw error;
        }
    }

    /**
     * Atomically increment usage stats
     */
    /**
     * Update daily usage statistics
     * Stores usage in a nested map: usageHistory[date] = { reads, writes, updated }
     */
    async updateDailyUsage(userId: string, date: string, reads: number, writes: number): Promise<void> {
        try {
            const client = this.getClient();

            // Note: Updating nested maps atomically in DynamoDB requires the parent map to exist
            // OR using a clever update expression.
            // We'll assume usageHistory might effectively correspond to a map.
            // Safe approach: Use "SET #h.#d.reads = if_not_exists(#h.#d.reads, :zero) + :r"

            const params: UpdateCommandInput = {
                TableName: this.tableName,
                Key: { userId },
                UpdateExpression: `
                    SET #h.#d.readOps = if_not_exists(#h.#d.readOps, :zero) + :r,
                        #h.#d.writeOps = if_not_exists(#h.#d.writeOps, :zero) + :w,
                        #h.#d.lastUpdated = :now
                `,
                ExpressionAttributeNames: {
                    '#h': 'usageHistory',
                    '#d': date
                },
                ExpressionAttributeValues: {
                    ':r': reads,
                    ':w': writes,
                    ':zero': 0,
                    ':now': new Date().toISOString()
                }
            };

            // However, if 'usageHistory' itself doesn't exist, the above fails.
            // We need to ensure 'usageHistory' exists.
            // A common pattern is to try update, catch "ValidationException" (path not found), then init map.
            // But lets try a more robust expression that initializes if needed if possible.
            // DynamoDB can't init parent and child in one go easily if parent missing.

            // Optimization: Just check/init once per session? No, tracked stateless.
            // Strategy: Try update. If fail, SET usageHistory = empty map, then retry.

            try {
                await client.send(new UpdateCommand(params));
            } catch (err: any) {
                // Check for "document path provided in the update expression is invalid for update"
                if (err.name === 'ValidationException' || err.message.includes('document path')) {
                    // Parent map probably missing. Initialize it.
                    const initParams: UpdateCommandInput = {
                        TableName: this.tableName,
                        Key: { userId },
                        UpdateExpression: 'SET #h = if_not_exists(#h, :empty)',
                        ExpressionAttributeNames: { '#h': 'usageHistory' },
                        ExpressionAttributeValues: { ':empty': {} }
                    };
                    await client.send(new UpdateCommand(initParams));
                    // Retry original update
                    await client.send(new UpdateCommand(params));
                } else {
                    throw err;
                }
            }
        } catch (error) {
            console.error('Error in updateDailyUsage:', error);
        }
    }
}

// Export singleton instance
export const awsUserService = new AWSUserService();
